import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, ArrowLeft } from 'lucide-react';
import { api, timeAgo } from '../lib/api';
import { useAuth } from '../lib/store';
import { getSocket } from '../lib/socket';
import PostCard from '../components/PostCard';
import { Avatar, SkeletonList, Empty } from '../components/ui';

export default function PostPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [body, setBody] = useState('');
  const [comments, setComments] = useState([]);

  const { data: post, isLoading } = useQuery({ queryKey: ['post', id], queryFn: () => api.get(`/posts/${id}`).then(r => r.data) });

  useEffect(() => {
    // track share click for referral points
    const token = sp.get('s');
    if (token) api.get(`/posts/share/${token}/click`).catch(() => {});
    api.get(`/posts/${id}/comments`).then(r => setComments(r.data));
    const s = getSocket(); if (!s) return;
    s.emit('join', `post:${id}`);
    const onC = (c) => setComments(v => [...v, c]);
    s.on('comment:new', onC);
    return () => { s.off('comment:new', onC); s.emit('leave', `post:${id}`); };
  }, [id]);

  const commentM = useMutation({
    mutationFn: () => api.post(`/posts/${id}/comments`, { body }),
    onSuccess: () => { setBody(''); qc.invalidateQueries(['post', id]); },
  });

  if (isLoading || !post) return <SkeletonList n={2} h="h-60" />;

  return (
    <div className="max-w-lg mx-auto space-y-3 animate-fade-up">
      <Link to="/feed" className="btn-ghost btn-sm"><ArrowLeft size={14} /> Back to feed</Link>
      <PostCard post={post} />

      <div className="card p-4">
        <p className="label">Comments ({comments.length})</p>
        <div className="flex gap-2 mb-4">
          <Avatar src={user?.avatar_url} name={user?.username} size={34} />
          <input className="input !py-2.5" placeholder="Write a comment…" value={body}
            onChange={e => setBody(e.target.value)} onKeyDown={e => e.key === 'Enter' && body && commentM.mutate()} />
          <button onClick={() => commentM.mutate()} disabled={!body.trim()} className="btn-primary !px-3"><Send size={15} /></button>
        </div>

        {!comments.length ? <Empty title="No comments yet" sub="Start the conversation." />
          : <div className="space-y-3">
            {comments.map(c => (
              <div key={c.id} className="flex gap-2.5">
                <Link to={`/u/${c.username}`}><Avatar src={c.avatar_url} name={c.username} size={32} /></Link>
                <div className="flex-1">
                  <div className="bg-ink-50 rounded-2xl rounded-tl-sm px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <Link to={`/u/${c.username}`} className="font-bold text-xs hover:underline">
                        {c.display_name || c.username}</Link>
                      {c.level && <span className="text-[9px] font-bold text-ink-400">L{c.level}</span>}
                    </div>
                    <p className="text-sm text-ink-700 mt-0.5">{c.body}</p>
                  </div>
                  <p className="text-[10px] text-ink-400 font-semibold mt-1 px-2">{timeAgo(c.created_at)}</p>
                </div>
              </div>))}
          </div>}
      </div>
    </div>
  );
}